import React, { useState, FormEvent, useEffect } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import { Industries, IndustryData } from "./types";
import { FaCoins, FaChartPie, FaInfoCircle } from "react-icons/fa";
import { Box } from "./CpmCalculatorStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const industries: Industries = {
  ecommerce: {
    name: "eCommerce",
    benchmarkLow: 2,
    benchmarkHigh: 5,
    generalRecommendations: [
      "Optimize product-specific ad targeting to reach users with high purchase intent",
      "Use dynamic retargeting ads to convert abandoned cart visitors",
      "Test different platforms like Google Ads, Meta Ads, or TikTok to find better CPM rates",
    ],
    highCpmRecommendations: [
      "Narrow down audience targeting to focus on most valuable segments",
      "Analyze and pause underperforming ad placements",
      "Consider shifting budget to better performing platforms",
      "Test different ad formats to find more cost-effective options",
    ],
    lowCpmRecommendations: [
      "Scale successful campaigns to maintain efficient CPM",
      "Document successful targeting strategies for future campaigns",
      "Consider increasing budget for high-performing segments",
      "Explore similar audiences to maintain performance",
    ],
  },
  saas: {
    name: "SaaS",
    benchmarkLow: 5,
    benchmarkHigh: 15,
    generalRecommendations: [
      "Create educational content-based ads to attract qualified leads",
      "Focus on lead nurturing campaigns for trial sign-ups",
      "Optimize landing pages for better conversion rates",
    ],
    highCpmRecommendations: [
      "Segment audience targeting more precisely",
      "Review and optimize ad scheduling",
      "Test different ad formats for better engagement",
      "Consider reducing bids on expensive keywords",
    ],
    lowCpmRecommendations: [
      "Increase budget for best-performing campaigns",
      "Expand to similar audience segments",
      "Test new ad formats while maintaining efficiency",
      "Document successful campaign strategies",
    ],
  },
  publishing: {
    name: "Publishing/Media",
    benchmarkLow: 3,
    benchmarkHigh: 8,
    generalRecommendations: [
      "Focus on content-driven advertising to engage readers",
      "Use native advertising formats for better user experience",
      "Optimize ad placements based on user engagement metrics",
    ],
    highCpmRecommendations: [
      "Review and adjust ad placement strategies",
      "Test different content formats and themes",
      "Optimize audience targeting parameters",
      "Consider reducing premium placement usage",
    ],
    lowCpmRecommendations: [
      "Scale successful content campaigns",
      "Expand to related content categories",
      "Increase investment in high-performing formats",
      "Document effective content strategies",
    ],
  },
  finance: {
    name: "Finance",
    benchmarkLow: 6,
    benchmarkHigh: 18,
    generalRecommendations: [
      "Focus on trust-building content in ads",
      "Implement strict compliance measures",
      "Target high-intent keywords",
    ],
    highCpmRecommendations: [
      "Review and optimize bidding strategies",
      "Focus on most profitable customer segments",
      "Test different ad formats for cost efficiency",
      "Consider reducing exposure in expensive placements",
    ],
    lowCpmRecommendations: [
      "Scale successful campaigns carefully",
      "Expand to similar qualified audiences",
      "Increase budget for high-performing segments",
      "Document compliant advertising strategies",
    ],
  },
  travel: {
    name: "Travel & Hospitality",
    benchmarkLow: 4,
    benchmarkHigh: 10,
    generalRecommendations: [
      "Use high-quality destination imagery in ads",
      "Implement seasonal campaign strategies",
      "Target users based on travel intent signals",
    ],
    highCpmRecommendations: [
      "Optimize targeting for peak travel seasons",
      "Review and adjust bidding strategies",
      "Test different ad formats and placements",
      "Consider reducing exposure during high-cost periods",
    ],
    lowCpmRecommendations: [
      "Scale successful destination campaigns",
      "Expand to related travel categories",
      "Increase budget for high-performing seasons",
      "Document successful targeting strategies",
    ],
  },
  healthcare: {
    name: "Healthcare",
    benchmarkLow: 5,
    benchmarkHigh: 14,
    generalRecommendations: [
      "Ensure HIPAA compliance in all ads",
      "Focus on educational content",
      "Target specific healthcare interests",
    ],
    highCpmRecommendations: [
      "Review targeting parameters for efficiency",
      "Test different ad formats within compliance",
      "Optimize ad scheduling for better rates",
      "Consider reducing exposure in premium placements",
    ],
    lowCpmRecommendations: [
      "Scale compliant campaigns carefully",
      "Expand to related healthcare segments",
      "Increase budget for effective campaigns",
      "Document successful compliance strategies",
    ],
  },
  education: {
    name: "Education",
    benchmarkLow: 3,
    benchmarkHigh: 9,
    generalRecommendations: [
      "Create ads highlighting student success stories",
      "Target campaigns based on enrollment periods",
      "Focus on educational value propositions",
    ],
    highCpmRecommendations: [
      "Refine audience targeting parameters",
      "Optimize ad scheduling around key periods",
      "Test different ad formats for engagement",
      "Review and adjust bidding strategies",
    ],
    lowCpmRecommendations: [
      "Scale successful program campaigns",
      "Expand to related education segments",
      "Increase budget for peak enrollment periods",
      "Document effective targeting strategies",
    ],
  },
  retail: {
    name: "Retail",
    benchmarkLow: 1.5,
    benchmarkHigh: 6,
    generalRecommendations: [
      "Use dynamic product ads effectively",
      "Implement seasonal campaign strategies",
      "Focus on local targeting when applicable",
    ],
    highCpmRecommendations: [
      "Optimize audience targeting parameters",
      "Review and adjust bid strategies",
      "Test different ad formats and placements",
      "Consider reducing exposure during peak hours",
    ],
    lowCpmRecommendations: [
      "Scale successful product campaigns",
      "Expand to similar product categories",
      "Increase budget for best-performing ads",
      "Document successful targeting strategies",
    ],
  },
  b2b: {
    name: "B2B",
    benchmarkLow: 6,
    benchmarkHigh: 20,
    generalRecommendations: [
      "Focus on value proposition in ad messaging",
      "Create content addressing specific pain points",
      "Target decision-makers with precise parameters",
    ],
    highCpmRecommendations: [
      "Review and optimize targeting criteria",
      "Test different ad formats for engagement",
      "Adjust bidding strategies for efficiency",
      "Consider reducing premium placement usage",
    ],
    lowCpmRecommendations: [
      "Scale successful lead generation campaigns",
      "Expand to related business segments",
      "Increase budget for high-converting campaigns",
      "Document effective B2B strategies",
    ],
  },
  entertainment: {
    name: "Entertainment",
    benchmarkLow: 2,
    benchmarkHigh: 7,
    generalRecommendations: [
      "Use eye-catching visuals and video content",
      "Create ads that showcase unique experiences",
      "Target based on entertainment preferences",
    ],
    highCpmRecommendations: [
      "Optimize targeting for peak entertainment periods",
      "Review and adjust creative strategies",
      "Test different ad formats and placements",
      "Consider reducing premium placement usage",
    ],
    lowCpmRecommendations: [
      "Scale successful entertainment campaigns",
      "Expand to related entertainment categories",
      "Increase budget for high-performing content",
      "Document effective targeting strategies",
    ],
  },
};

const CPMCalculator: React.FC = () => {
  const [adSpend, setAdSpend] = useState<number | "">("");
  const [impressions, setImpressions] = useState<number | "">("");
  const [industry, setIndustry] = useState<string>("");
  const [cpm, setCpm] = useState<number | null>(null);
  const [status, setStatus] = useState<string>("");
  const [performanceCategory, setPerformanceCategory] = useState<string>("");
  const [benchmarkAnalysis, setBenchmarkAnalysis] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [showResults, setShowResults] = useState<boolean>(false);

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [showResults]);
  const handleCalculate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setShowResults(false);

    const adSpendValue = Number(adSpend);
    const impressionsValue = Number(impressions);
    const selectedIndustry = industries[industry as keyof Industries];

    if (!adSpendValue || !impressionsValue || !industry) {
      setError("Please fill in all fields");
      return;
    }

    if (adSpendValue <= 0 || impressionsValue <= 0) {
      setError("Values must be greater than 0");
      return;
    }

    const calculatedCpm = (adSpendValue / impressionsValue) * 1000;
    setCpm(calculatedCpm);

    let newStatus = "";
    let newBenchmarkAnalysis = "";
    let newPerformanceCategory = "";

    const benchmarkAverage =
      (selectedIndustry.benchmarkLow + selectedIndustry.benchmarkHigh) / 2;

    if (calculatedCpm < 2.5) {
      newStatus = "Efficient";
      newPerformanceCategory = "Excellent";
      const percentDiff = Math.abs(
        ((benchmarkAverage - calculatedCpm) / benchmarkAverage) * 100
      ).toFixed(1);
      newBenchmarkAnalysis = `${percentDiff}% more efficient than average`;
    } else if (calculatedCpm >= 2.5 && calculatedCpm <= 4.5) {
      newStatus = "Within Benchmark";
      newPerformanceCategory = "Good";
      const percentDiff = Math.abs(
        ((calculatedCpm - benchmarkAverage) / benchmarkAverage) * 100
      ).toFixed(1);
      if (
        Math.abs(calculatedCpm - benchmarkAverage) / benchmarkAverage <
        0.05
      ) {
        newBenchmarkAnalysis = "Optimal performance";
      } else if (calculatedCpm < benchmarkAverage) {
        newBenchmarkAnalysis = `${percentDiff}% more efficient than average`;
      } else {
        newBenchmarkAnalysis = `${percentDiff}% above average`;
      }
    } else {
      newStatus = "Above Benchmark";
      newPerformanceCategory = "Poor";
      const percentDiff = Math.abs(
        ((calculatedCpm - benchmarkAverage) / benchmarkAverage) * 100
      ).toFixed(1);
      newBenchmarkAnalysis = `${percentDiff}% above average`;
    }

    setStatus(newStatus);
    setPerformanceCategory(newPerformanceCategory);
    setBenchmarkAnalysis(newBenchmarkAnalysis);
    setShowResults(true);
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };

  const handleReset = () => {
    setAdSpend("");
    setImpressions("");
    setIndustry("");
    setCpm(null);
    setStatus("");
    setPerformanceCategory("");
    setBenchmarkAnalysis("");
    setError("");
    setShowResults(false);
  };

  const getPerformanceBadgeText = (category: string): string => {
    switch (category) {
      case "Excellent":
        return "Excellent CPM Performance";
      case "Good":
        return "Good CPM Performance";
      case "Poor":
        return "Bad CPM Performance";
      default:
        return "";
    }
  };

  const getRecommendations = () => {
    if (!industry || !performanceCategory)
      return { context: "", recommendations: [] };

    const selectedIndustry = industries[industry as keyof Industries];
    let recommendations: string[] = [];
    let context = "";

    if (performanceCategory === "Poor") {
      recommendations = [
        ...selectedIndustry.highCpmRecommendations,
        ...selectedIndustry.generalRecommendations,
      ];
      context = "Your CPM is Poor. Here are recommendations to optimize costs:";
    } else if (performanceCategory === "Excellent") {
      recommendations = [
        ...selectedIndustry.lowCpmRecommendations,
        ...selectedIndustry.generalRecommendations,
      ];
      context =
        "Your CPM is Excellent. Here are recommendations to maintain and scale:";
    } else {
      recommendations = [...selectedIndustry.generalRecommendations];
      context =
        "Your CPM is Good. Here are recommendations for continued success:";
    }

    return { context, recommendations };
  };

  const renderBenchmarkInfo = () => {
    if (!industry || !showResults) return null;

    const selectedIndustry = industries[industry as keyof Industries];
    const benchmarkAverage =
      (selectedIndustry.benchmarkLow + selectedIndustry.benchmarkHigh) / 2;
    return (
      <Card className="p-3 mb-4 cpm-bg-light border-0">
        <div className="d-flex align-items-center gap-2 mb-2">
          <FaChartPie className="cpm-text-primary" size={20} />
          <h3 className="cpm-fs-5 fw-semibold mb-0">Industry Benchmark</h3>
        </div>
        <p className="text-muted">
          The typical CPM range for {selectedIndustry.name} is $
          {selectedIndustry.benchmarkLow.toFixed(2)} - $
          {selectedIndustry.benchmarkHigh.toFixed(2)}. The industry average is $
          {benchmarkAverage.toFixed(2)}.
        </p>
      </Card>
    );
  };

  const renderResults = () => {
    if (!showResults || !cpm || !status || !performanceCategory) return null;

    const performanceClass = `cpm-${performanceCategory.toLowerCase()}`;
    return (
      <div>
        <Card className="formCardResponsive">
          <Card.Body>
            <h2 className="cpm-fs-3 fw-bold mb-4">Results</h2>
            <Row className="mb-4 cpm-result-card">
              <Col xs={12} md={3} className="cpm-result-row p-3 text-center">
                <span className="d-block text-muted mb-2">Your CPM</span>
                <span className={`cpm-fs-2 fw-bold ${performanceClass}`}>
                  ${cpm.toFixed(2)}
                </span>
                <div>
                  <Badge
                    bg={
                      performanceCategory === "Poor"
                        ? "danger"
                        : performanceCategory === "Excellent"
                        ? "success"
                        : "warning"
                    }
                    className="mt-2"
                    style={{ textWrap: "unset" }}
                  >
                    {getPerformanceBadgeText(performanceCategory)}
                  </Badge>
                </div>
              </Col>
              <Col xs={12} md={3} className="cpm-result-row p-3 text-center">
                <span className="d-block text-muted mb-2">Status</span>
                <span className={`cpm-fs-4 fw-semibold ${performanceClass}`}>
                  {status}
                </span>
              </Col>
              <Col xs={12} md={3} className="cpm-result-row p-3 text-center">
                <span className="d-block text-muted mb-2">
                  Performance Category
                </span>
                <span className={`cpm-fs-4 fw-semibold ${performanceClass}`}>
                  {performanceCategory}
                </span>
              </Col>
              <Col xs={12} md={3} className="cpm-result-row p-3 text-center">
                <span className="d-block text-muted mb-2">
                  Benchmark Analysis
                </span>
                <span className={`cpm-fs-4 fw-semibold ${performanceClass}`}>
                  {benchmarkAnalysis}
                </span>
              </Col>
            </Row>

            {renderBenchmarkInfo()}

            <div className="cpm-recommendations-section formCardResponsive">
              <h2 className="cpm-fs-3 fw-bold mb-4">Recommendations</h2>
              <div className="cpm-recommendations-context p-3 mb-3 rounded">
                {getRecommendations().context}
              </div>
              {getRecommendations().recommendations.map((rec, index) => (
                <div
                  key={index}
                  className="cpm-recommendation-item d-flex align-items-start formCardResponsive1rem cpm-bg-light rounded mb-2"
                >
                  <div className="cpm-recommendation-number me-3 d-flex align-items-center justify-content-center rounded-circle cpm-bg-light cpm-text-primary fw-medium">
                    {index + 1}
                  </div>
                  <div>{rec}</div>
                </div>
              ))}
            </div>

            <div className="cpm-help-text d-flex align-items-center gap-2 p-3 mt-4 rounded cpm-bg-light">
              <FaInfoCircle className="text-muted" />
              <span>
                CPM is calculated using the formula: (Total Ad Spend / Total
                Impressions) × 1000
              </span>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <Box>
      <Container
        className={`cpm-container ${
          !isInIframe ? "normalFont" : "interDisplay"
        } ${!isInIframe ? "py-5" : "py-1"}`}
      >
        {!isInIframe && (
          <ToolsHeader
            title={"CPM Calculator"}
            description="Calculate Cost Per Mille (CPM) accurately to optimize and
                measure your ad impressions."
          />
        )}

        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <div className="cpm-main-box">
              <Card className="mb-4 formCardResponsive">
                <Card.Body>
                  {error && (
                    <div className="cpm-error-message p-3 mb-3 rounded text-danger">
                      {error}
                    </div>
                  )}
                  <Form onSubmit={handleCalculate}>
                    <Form.Group className="mb-3">
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={adSpend}
                        onChange={(e) =>
                          setAdSpend(
                            e.target.value ? Number(e.target.value) : ""
                          )
                        }
                        placeholder="Total Ad Spend (USD)"
                        required
                        className="cpm-custom-input"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Control
                        type="number"
                        min="1"
                        value={impressions}
                        onChange={(e) =>
                          setImpressions(
                            e.target.value ? Number(e.target.value) : ""
                          )
                        }
                        placeholder="Total Impressions"
                        required
                        className="cpm-custom-input"
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Select
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        required
                        className="cpm-custom-select"
                      >
                        <option value="" disabled>
                          Industry
                        </option>
                        {Object.entries(industries).map(([key, ind]) => (
                          <option key={key} value={key}>
                            {ind.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <div className="d-flex gap-3 cpm-action-buttons">
                      <Button
                        type="submit"
                        className="flex-grow-1 cpm-custom-button .responsive_480"
                      >
                        Calculate CPM
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        className="cpm-custom-button cpm-secondary-button .responsive_480"
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>

              {renderResults()}
            </div>
          </Col>
        </Row>
      </Container>
    </Box>
  );
};

export default CPMCalculator;
